import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Car, TrendingUp, Wrench, FileText, Check, X, Wallet, Percent, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export default function InvestorDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sRes, vRes, qRes] = await Promise.all([api.get("/dashboard/stats"), api.get("/vehicles"), api.get("/quotes")]);
        setStats(sRes.data); setVehicles(vRes.data); setQuotes(qRes.data);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleQuote = async (quoteId, action) => {
    try {
      await api.put(`/quotes/${quoteId}/validate`, { action });
      toast.success(action === "valide" ? "Devis valide" : "Devis refuse");
      const [qRes, sRes] = await Promise.all([api.get("/quotes"), api.get("/dashboard/stats")]);
      setQuotes(qRes.data); setStats(sRes.data);
    } catch (e) { toast.error("Erreur"); }
  };

  if (loading) return <div className="text-slate-500">Chargement...</div>;

  const statCards = [
    { label: "Vehicules", value: stats.total_vehicules || 0, icon: Car, color: "text-[#0F52BA]", bg: "bg-blue-50" },
    { label: "Actifs", value: stats.vehicules_actifs || 0, icon: Car, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Immobilises", value: stats.vehicules_immobilises || 0, icon: Wrench, color: "text-red-600", bg: "bg-red-50" },
    { label: "Investissement total", value: `${(stats.investissement_total || 0).toLocaleString("fr-FR")} Frc`, icon: Wallet, color: "text-slate-700", bg: "bg-slate-100" },
    { label: "Recettes totales", value: `${(stats.recettes_total || 0).toLocaleString("fr-FR")} Frc`, icon: TrendingUp, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Reparations totales", value: `${(stats.reparations_total || 0).toLocaleString("fr-FR")} Frc`, icon: Wrench, color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Benefice net", value: `${(stats.benefice_net_total || 0).toLocaleString("fr-FR")} Frc`, icon: (stats.benefice_net_total || 0) >= 0 ? ArrowUpRight : ArrowDownRight, color: (stats.benefice_net_total || 0) >= 0 ? "text-emerald-600" : "text-red-600", bg: (stats.benefice_net_total || 0) >= 0 ? "bg-emerald-50" : "bg-red-50" },
    { label: "Rentabilite globale", value: `${stats.rentabilite_globale || 0} %`, icon: Percent, color: (stats.rentabilite_globale || 0) >= 0 ? "text-[#0F52BA]" : "text-red-600", bg: "bg-blue-50" },
    { label: "Devis en attente", value: stats.devis_en_attente || 0, icon: FileText, color: "text-[#0F52BA]", bg: "bg-blue-50" },
  ];

  const pendingQuotes = quotes.filter(q => q.statut === "en_attente");

  return (
    <div data-testid="investor-dashboard">
      <div className="page-header">
        <div>
          <h1 className="page-title">Tableau de bord Investisseur</h1>
          <p className="text-sm text-slate-500 mt-1">Bienvenue, {user?.prenom} {user?.nom}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8" data-testid="investor-stats">
        {statCards.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="stat-card">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}><Icon className={`w-5 h-5 ${s.color}`} /></div>
              </div>
              <p className="text-2xl font-bold text-slate-900 font-heading">{s.value}</p>
              <p className="text-xs text-slate-500 mt-1">{s.label}</p>
            </div>
          );
        })}
      </div>

      {stats.chart_data && stats.chart_data.length > 0 && (
        <Card className="mb-8" data-testid="investor-chart">
          <CardHeader><CardTitle className="text-lg font-heading text-slate-900">Recettes des 7 derniers jours</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.chart_data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#64748B" }} tickFormatter={(v) => { const d = new Date(v); return `${d.getDate()}/${d.getMonth()+1}`; }} />
                  <YAxis tick={{ fontSize: 12, fill: "#64748B" }} />
                  <Tooltip formatter={(v) => [`${v} Frc`, "Recettes"]} />
                  <Bar dataKey="montant" fill="#0F52BA" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Vehicles with Rentabilité */}
      <Card className="mb-8" data-testid="investor-vehicles">
        <CardHeader><CardTitle className="text-lg font-heading text-slate-900">Mes vehicules — Rentabilite</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50">
                <TableHead className="font-semibold text-slate-600">Vehicule</TableHead>
                <TableHead className="font-semibold text-slate-600">Immatriculation</TableHead>
                <TableHead className="font-semibold text-slate-600">Statut</TableHead>
                <TableHead className="font-semibold text-slate-600">Prix acquisition</TableHead>
                <TableHead className="font-semibold text-slate-600">Recettes</TableHead>
                <TableHead className="font-semibold text-slate-600">Reparations</TableHead>
                <TableHead className="font-semibold text-slate-600">Benefice</TableHead>
                <TableHead className="font-semibold text-slate-600">Rentabilite</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vehicles.map(v => {
                const vs = (stats.vehicle_stats || []).find(s => s.vehicule_id === v.id) || {};
                const benefice = vs.resultat || 0;
                const rentPct = vs.rentabilite_pct || 0;
                const amortPct = Math.min(vs.amortissement_pct || 0, 100);
                const prixAcq = vs.prix_acquisition || v.prix_acquisition || 0;
                return (
                  <TableRow key={v.id} data-testid={`vehicle-row-${v.id}`}>
                    <TableCell className="font-medium text-slate-900">{v.marque} {v.modele}</TableCell>
                    <TableCell className="text-slate-600">{v.immatriculation}</TableCell>
                    <TableCell><Badge variant="outline" className={v.statut === "actif" ? "status-actif" : "status-immobilise"}>{v.statut}</Badge></TableCell>
                    <TableCell className="text-slate-600">{prixAcq.toLocaleString("fr-FR")} Frc</TableCell>
                    <TableCell className="text-emerald-600 font-medium">{(vs.recettes || 0).toLocaleString("fr-FR")} Frc</TableCell>
                    <TableCell className="text-red-600 font-medium">{(vs.reparations || 0).toLocaleString("fr-FR")} Frc</TableCell>
                    <TableCell className={`font-bold ${benefice >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid={`benefice-${v.id}`}>
                      {benefice >= 0 ? "+" : ""}{benefice.toLocaleString("fr-FR")} Frc
                    </TableCell>
                    <TableCell data-testid={`rentabilite-${v.id}`}>
                      <div className="min-w-[120px]">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-sm font-bold ${rentPct >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                            {rentPct >= 0 ? "+" : ""}{rentPct}%
                          </span>
                        </div>
                        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${amortPct >= 100 ? "bg-emerald-500" : amortPct >= 50 ? "bg-blue-500" : "bg-amber-500"}`}
                            style={{ width: `${Math.min(amortPct, 100)}%` }}
                          />
                        </div>
                        <p className="text-[10px] text-slate-400 mt-0.5">{amortPct >= 100 ? "Amorti" : `${amortPct}% amorti`}</p>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pending Quotes */}
      {pendingQuotes.length > 0 && (
        <Card data-testid="pending-quotes">
          <CardHeader><CardTitle className="text-lg font-heading text-slate-900">Devis en attente de validation</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold text-slate-600">Description</TableHead>
                  <TableHead className="font-semibold text-slate-600">Pieces</TableHead>
                  <TableHead className="font-semibold text-slate-600">Total</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingQuotes.map(q => (
                  <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                    <TableCell className="text-slate-700">{q.description}</TableCell>
                    <TableCell className="text-slate-600">{(q.pieces || []).map(p => p.nom).join(", ") || "-"}</TableCell>
                    <TableCell className="font-medium text-slate-900">{q.total} Frc</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleQuote(q.id, "valide")} data-testid={`btn-validate-${q.id}`}><Check className="w-4 h-4 mr-1" />Valider</Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleQuote(q.id, "refuse")} data-testid={`btn-refuse-${q.id}`}><X className="w-4 h-4 mr-1" />Refuser</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
